import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';

import { useGeneratePropertySummary } from '~/hooks/apis/property-summary/useGeneratePropertySummary';

import { useNextBestActionFiles } from './NextBestActionFilesContext';
import UploadArea from './UploadArea';
import {
  ParityData,
  SalesforceData,
  VnsData,
  parseParityReport,
  parseSalesforceReport,
  parseVnsReport,
} from './parseNextBestActionFiles';

interface Props {
  open: boolean;
  onClose: () => void;
  propertyName: string;
}

type ParseState<T> =
  | { status: 'idle' }
  | { status: 'parsing' }
  | { status: 'matched'; data: T }
  | { status: 'not-found' }
  | { status: 'error'; message: string };

const RESULT_SECTIONS = [
  { key: 'CAM' },
  { key: 'SF Opp' },
  { key: "Yesterday's parity data" },
  { key: 'VNS' },
  { key: 'Recommended next action' },
];

export function parseResultSections(text: string): Array<{ label: string; content: string }> {
  const keys = RESULT_SECTIONS.map((s) => s.key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const pattern = new RegExp(`(${keys.join('|')}):\\s*`, 'g');

  const parts = text.split(pattern);
  // split with capture group returns: [pre, label1, content1, label2, content2, ...]
  const parsed: Array<{ label: string; content: string }> = [];
  for (let i = 1; i < parts.length; i += 2) {
    const label = parts[i]?.trim();
    const content = parts[i + 1]?.trim().replace(/\s+/g, ' ') ?? '';
    if (label) parsed.push({ label, content });
  }

  return parsed.length > 0 ? parsed : [{ label: '', content: text }];
}

export default function NextBestActionModal({ open, onClose, propertyName }: Props) {
  const { parityFile, salesforceFile, vnsFile, setParityFile, setSalesforceFile, setVnsFile, clearFiles } =
    useNextBestActionFiles();
  const [copied, setCopied] = useState(false);

  const [vnsState, setVnsState] = useState<ParseState<VnsData>>({ status: 'idle' });
  const [sfState, setSfState] = useState<ParseState<SalesforceData>>({ status: 'idle' });
  const [parityState, setParityState] = useState<ParseState<ParityData>>({ status: 'idle' });

  const parityReportRef = useRef<HTMLInputElement>(null);
  const salesforceReportRef = useRef<HTMLInputElement>(null);
  const vnsRef = useRef<HTMLInputElement>(null);
  const lastParsedRef = useRef<{
    propertyName: string;
    vnsFile: File | null;
    salesforceFile: File | null;
  } | null>(null);

  const { mutate, isPending, isError, error, data, reset } = useGeneratePropertySummary();

  const vnsData = vnsState.status === 'matched' ? vnsState.data : null;
  const canGenerate =
    parityFile !== null &&
    salesforceFile !== null &&
    vnsFile !== null &&
    vnsState.status === 'matched' &&
    (sfState.status === 'matched' || sfState.status === 'not-found') &&
    (parityState.status === 'matched' || parityState.status === 'not-found');
  const generateBlockReason =
    canGenerate || isPending
      ? null
      : !vnsFile || !salesforceFile || !parityFile
        ? 'Upload all three files to generate'
        : vnsState.status === 'parsing' || sfState.status === 'parsing' || parityState.status === 'parsing'
          ? 'Waiting for files to finish parsing'
          : vnsState.status === 'not-found'
            ? 'VNS file did not match this property'
            : vnsState.status === 'error'
              ? 'VNS file could not be parsed — try re-uploading'
              : parityState.status === 'error'
                ? 'Parity file could not be parsed — try re-uploading'
                : sfState.status === 'error'
                  ? 'Salesforce file could not be parsed — try re-uploading'
                  : null;
  const sections = useMemo(() => (data ? parseResultSections(data.result) : []), [data]);

  const parseVns = useCallback(
    async (file: File) => {
      setVnsState({ status: 'parsing' });
      try {
        const text = await file.text();
        setVnsState({ status: 'matched', data: parseVnsReport(text, propertyName) });
      } catch (err) {
        setVnsState({ status: 'error', message: err instanceof Error ? err.message : 'Failed to parse VNS file.' });
      }
    },
    [propertyName],
  );

  const parseSf = useCallback(
    async (file: File) => {
      setSfState({ status: 'parsing' });
      try {
        const buffer = await file.arrayBuffer();
        const result = parseSalesforceReport(buffer, propertyName);
        setSfState(result ? { status: 'matched', data: result } : { status: 'not-found' });
      } catch (err) {
        setSfState({
          status: 'error',
          message: err instanceof Error ? err.message : 'Failed to parse Salesforce file.',
        });
      }
    },
    [propertyName],
  );

  const handleVnsChange = useCallback(
    async (file: File | null) => {
      setVnsFile(file);
      if (!file) {
        setVnsState({ status: 'idle' });
        return;
      }
      await parseVns(file);
    },
    [setVnsFile, parseVns],
  );

  const handleSfChange = useCallback(
    async (file: File | null) => {
      setSalesforceFile(file);
      if (!file) {
        setSfState({ status: 'idle' });
        return;
      }
      await parseSf(file);
    },
    [setSalesforceFile, parseSf],
  );

  const handleParityChange = useCallback(
    (file: File | null) => {
      setParityFile(file);
      if (!file) setParityState({ status: 'idle' });
    },
    [setParityFile],
  );

  useEffect(() => {
    if (!parityFile || !vnsData) {
      if (parityFile) setParityState({ status: 'idle' });
      return;
    }
    let cancelled = false;
    setParityState({ status: 'parsing' });
    parseParityReport(parityFile, vnsData.offerId)
      .then((result) => {
        if (!cancelled) setParityState(result ? { status: 'matched', data: result } : { status: 'not-found' });
      })
      .catch((err) => {
        if (!cancelled)
          setParityState({
            status: 'error',
            message: err instanceof Error ? err.message : 'Failed to parse parity report.',
          });
      });
    return () => {
      cancelled = true;
    };
  }, [parityFile, vnsData]);

  const resetParseStates = useCallback(() => {
    setVnsState({ status: 'idle' });
    setSfState({ status: 'idle' });
    setParityState({ status: 'idle' });
  }, []);

  const clearInputRefs = useCallback(() => {
    if (parityReportRef.current) parityReportRef.current.value = '';
    if (salesforceReportRef.current) salesforceReportRef.current.value = '';
    if (vnsRef.current) vnsRef.current.value = '';
  }, []);

  // When the modal opens, re-parse if the property or either file changed since last parse.
  // Skipped when reopening the same property with the same files to avoid flicker.
  // Parity re-parsing is handled by the [parityFile, vnsData] effect above once vnsData resolves.
  useEffect(() => {
    if (!open) return;
    const last = lastParsedRef.current;
    const unchanged =
      last !== null &&
      last.propertyName === propertyName &&
      last.vnsFile === vnsFile &&
      last.salesforceFile === salesforceFile;
    if (unchanged) return;
    lastParsedRef.current = { propertyName, vnsFile, salesforceFile };
    resetParseStates();
    if (vnsFile) parseVns(vnsFile);
    if (salesforceFile) parseSf(salesforceFile);
  }, [open, propertyName, vnsFile, salesforceFile, parseVns, parseSf, resetParseStates]);

  const handleClose = useCallback(() => {
    setCopied(false);
    clearInputRefs();
    reset();
    onClose();
  }, [clearInputRefs, reset, onClose]);

  const handleClearFiles = useCallback(() => {
    clearFiles();
    lastParsedRef.current = null;
    resetParseStates();
    clearInputRefs();
    reset();
  }, [clearFiles, resetParseStates, clearInputRefs, reset]);

  const handleGenerate = useCallback(() => {
    if (!vnsData) return;
    reset();
    const { matchedName: _matchedName, ...vnsPayload } = vnsData;
    mutate({
      propertyName,
      vns: vnsPayload,
      ...(parityState.status === 'matched' ? { parity: parityState.data } : {}),
      ...(sfState.status === 'matched' ? { salesforce: sfState.data } : {}),
    });
  }, [vnsData, parityState, sfState, propertyName, mutate, reset]);

  const handleCopy = useCallback(() => {
    if (!data) return;
    navigator.clipboard.writeText(data.result);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [data]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 0.5 }}>
        <Typography variant="h6" fontWeight={700} component="span">
          Next Best Action
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {propertyName}
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 1.5 }}>
            <UploadArea
              label="Price Parity Report (.xlsx)"
              hint='Excel file (.xlsx) with an "LPC Exec" sheet containing Offer ID, Views, and parity columns.'
              file={parityFile}
              inputRef={parityReportRef}
              onChange={handleParityChange}
              accept=".xlsx"
              matchState={parityState}
              matchLabel={propertyName}
            />
            <UploadArea
              label="Salesforce Rate Parity Report"
              hint='CSV file with an "Opportunity Name" column matching the property name.'
              file={salesforceFile}
              inputRef={salesforceReportRef}
              onChange={handleSfChange}
              matchState={sfState}
              matchLabel={sfState.status === 'matched' ? sfState.data.opportunityName : undefined}
            />
            <UploadArea
              label="VNS Accommodation Overview"
              hint='CSV file with "Property Name" and "offer_id" columns matching this property.'
              file={vnsFile}
              inputRef={vnsRef}
              onChange={handleVnsChange}
              matchState={vnsState}
              matchLabel={vnsState.status === 'matched' ? vnsState.data.matchedName : undefined}
            />
          </Box>

          {isError && (
            <Alert severity="error">
              {error instanceof Error ? error.message : 'Failed to generate summary. Please try again.'}
            </Alert>
          )}

          {data && sections.length > 0 && (
            <>
              <Divider />
              <Stack spacing={0.75}>
                {sections.map((section, i) => {
                  const isRecommendation = section.label === 'Recommended next action';
                  return (
                    <Box
                      key={section.label || i}
                      sx={{
                        border: '1px solid',
                        borderColor: isRecommendation ? 'primary.main' : 'grey.200',
                        borderLeft: '3px solid',
                        borderLeftColor: isRecommendation ? 'primary.main' : 'grey.300',
                        borderRadius: '0 4px 4px 0',
                        bgcolor: isRecommendation ? 'primary.50' : 'common.white',
                        px: 1.5,
                        py: 1,
                      }}
                    >
                      {section.label && (
                        <Typography
                          variant="caption"
                          fontWeight={700}
                          color={isRecommendation ? 'primary.main' : 'text.secondary'}
                          sx={{ display: 'block', mb: 0.25 }}
                        >
                          {section.label.toUpperCase()}
                        </Typography>
                      )}
                      <Typography variant="body2" color="text.primary" fontWeight={isRecommendation ? 500 : 400}>
                        {section.content}
                      </Typography>
                    </Box>
                  );
                })}
              </Stack>
            </>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'space-between' }}>
        <Stack direction="row" spacing={1}>
          <Button onClick={handleClose}>Close</Button>
          {(parityFile || salesforceFile || vnsFile) && (
            <Button onClick={handleClearFiles} color="inherit" sx={{ color: 'text.secondary' }}>
              Clear files
            </Button>
          )}
        </Stack>
        <Stack direction="row" spacing={1}>
          {data && (
            <Tooltip title={copied ? 'Copied!' : 'Copy to clipboard'}>
              <Button
                startIcon={<ContentCopyIcon fontSize="small" />}
                onClick={handleCopy}
                color={copied ? 'success' : 'primary'}
              >
                {copied ? 'Copied' : 'Copy'}
              </Button>
            </Tooltip>
          )}
          <Tooltip title={generateBlockReason ?? ''} arrow>
            <span>
              <Button
                variant="contained"
                onClick={handleGenerate}
                disabled={!canGenerate || isPending}
                startIcon={isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
              >
                {isPending ? 'Generating...' : 'Generate'}
              </Button>
            </span>
          </Tooltip>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}
